import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Error boundary for video generation components
 * Provides graceful error handling and recovery options
 */
export class VideoGenerationErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      maxRetries: 3
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Video generation error:', error, errorInfo);
    
    // Log to analytics if available
    if (window.gtag) {
      window.gtag('event', 'video_generation_error', {
        error_message: error.message,
        error_stack: error.stack,
        component_stack: errorInfo.componentStack
      });
    }

    this.setState({
      error,
      errorInfo
    });
  }

  handleRetry = () => {
    const { retryCount, maxRetries } = this.state;
    
    if (retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
    } else {
      // Max retries reached
      this.setState({
        error: new Error('Maximum retry attempts reached. Please refresh the page.')
      });
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    });
  };

  render() {
    const { hasError, error, errorInfo, retryCount, maxRetries } = this.state;
    const { children, fallback, onError } = this.props;

    if (hasError) {
      if (fallback) {
        return React.cloneElement(fallback, {
          error,
          errorInfo,
          onRetry: this.handleRetry,
          onReset: this.handleReset,
          retryCount,
          maxRetries
        });
      }

      return (
        <div className="flex flex-col items-center justify-center p-8 text-center bg-red-50 rounded-lg border border-red-200">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Video Generation Error
          </h3>
          <p className="text-red-700 mb-4 max-w-md">
            {error?.message || 'An unexpected error occurred while generating your video.'}
          </p>
          
          {errorInfo && (
            <details className="mb-4 text-left max-w-md">
              <summary className="cursor-pointer text-sm text-red-600 hover:text-red-800">
                Technical Details
              </summary>
              <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto">
                {errorInfo.componentStack}
              </pre>
            </details>
          )}

          <div className="flex gap-3">
            {retryCount < maxRetries ? (
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry ({retryCount + 1}/{maxRetries})
              </button>
            ) : (
              <button
                onClick={this.handleReset}
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </button>
            )}
            
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Simple error fallback component
 */
export function VideoErrorFallback({ error, onRetry, onReset, retryCount, maxRetries }) {
  return (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
      <h4 className="font-medium text-gray-900 mb-2">Video Generation Failed</h4>
      <p className="text-sm text-gray-600 mb-4">
        {error?.message || 'Unable to generate video at this time.'}
      </p>
      
      <div className="flex gap-2">
        {retryCount < maxRetries ? (
          <button
            onClick={onRetry}
            className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        ) : (
          <button
            onClick={onReset}
            className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
